/**
 * C2 마물 전투 연출 팩 — 몹 직군·정예 접두 계약(게임 ↔ 렌더/VFX 공용 정본).
 *
 * 설계: 새 MobKind를 늘리지 않고 기존 4종(모델·판정 몸통) 위에 "직군(role)"을 덧씌운다.
 *  - 모델·애니·네트워크 kind 번호는 그대로(기존 GLB 재사용), 직군은 공격 방식만 바꾼다.
 *  - 투척형: 고블린 몸 + 포물선 투척(착탄 지점 원 텔레그래프 → 투사체 → 착탄 판정)
 *  - 돌진형: 고블린 몸 + 짧은 직선 돌진(선 텔레그래프, 멧돼지보다 짧고 잦음)
 *  - 소환형: 주술사 몸 + 소환 룬(피해 없음, 졸개 고블린 소환·상한 있음)
 * 정예 접두(eliteAffix)는 elite=1인 몹에만 붙는다: 신속(이동·선딜) · 보호막(첫 피해 흡수) · 분열(처치 때 작은 고블린 2).
 *
 * 렌더/VFX는 이 파일의 상수와 Ev.Mob* 이벤트, MobsView.role/eliteAffix/shieldFrac, GameView.mobShots만 읽는다.
 * 판정 모양은 언제나 텔레그래프 슬롯 기하와 같다(telegraph.ts 불변식 유지).
 */

/** 몹 직군. 0 = 기본(종류 고유 공격) */
export const MobRole = { Base: 0, Thrower: 1, Rusher: 2, Summoner: 3 } as const;
export type MobRoleId = (typeof MobRole)[keyof typeof MobRole];
export const MOB_ROLE_COUNT = 4;

/** 직군이 올라탈 수 있는 몸(MobKind). 다른 조합은 spawn에서 Base로 떨어진다 */
export const MOB_ROLE_BODY: readonly number[] = [-1, 0, 0, 3]; // Thrower·Rusher = Goblin, Summoner = Shaman

/** 정예 접두. 0 = 없음 */
export const EliteAffix = { None: 0, Swift: 1, Bulwark: 2, Splitting: 3 } as const;
export type EliteAffixId = (typeof EliteAffix)[keyof typeof EliteAffix];
export const ELITE_AFFIX_COUNT = 4;

/** 몹 투사체 풀(투척형). 렌더가 그대로 읽는 SoA */
export const MOB_SHOT_CAPACITY = 32;
export interface MobShotsView {
  /** 1 = 날아가는 중 */
  readonly alive: Uint8Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly z: Float32Array;
  readonly prevX: Float32Array;
  readonly prevY: Float32Array;
  readonly prevZ: Float32Array;
  /** 0..1 비행 진행(포물선 높이·회전 연출용) */
  readonly progress: Float32Array;
  /** 착탄 지점 */
  readonly targetX: Float32Array;
  readonly targetZ: Float32Array;
  /** 던진 몹 지역 색 선택용 MobRole(지금은 Thrower만) */
  readonly role: Uint8Array;
  /** 슬롯 세대(재사용 감지) */
  readonly gen: Uint16Array;
}

/**
 * 이벤트 페이로드 계약(번호는 shared/events.ts Ev에 등록).
 * MobSwing   x,z 몹 위치, a 몹 번호, b MobKind, c 바라보는 각(rad), d 1 = 정예 · flags = MobRole
 * MobDash    x,z 시작, a 몹 번호, b 0 시작 · 1 끝, c 각(rad), d 길이(m) · flags = MobRole
 * MobShot    x,y,z 출발, a 투사체 슬롯, b MobRole, c 각(rad), d 비행 틱
 * MobShotHit x,z 착탄, a 투사체 슬롯, b 1 = 영웅 맞음, c 반경(m)
 * MobSummon  x,z 소환자, a 몹 번호, b 0 룬 시작 · 1 소환 완료, c 소환 수, d 룬 반경(m)
 * MobAffix   x,z 몹, a 몹 번호, b EliteAffix, c 0 부여(스폰) · 1 보호막 깨짐 · 2 분열
 */
